import {cacheMemo, weakMemo} from '@packages/utilities/memo';
import {
  makeCell,
  makeColumnCoordinatesFromLocation,
  makeRowCoordinatesFromLocation,
} from '../utils';
import {CELL_DIRECTIONS, DRAG_DIRECTIONS, LAYOUT_AXIS} from '../constants';

import type {
  CellId,
  Cell,
  CellDirection,
  CellMetadata,
  TracksMetadata,
  LayoutMatrix,
  LayoutLocations,
} from '../types';
import computeLayoutMatrix from './computeLayoutMatrix';

// 50 items is somehow arbitrary, but it's unlikely that users will have more
// than 50 cells on the same page.
const CACHE_MAX_ITEMS = 50;
const cellIdMemo = cacheMemo(CACHE_MAX_ITEMS);

/**
 * Computes if a Cell can be dragged, resized in each direction, whether you
 * can add the row or column in each direction, and the start position index to add
 * the row or column.
 *
 * Example return value:
 * {
 *   left: { canDrag: true, startPositionIndex: 3, tracksMeta: { main: 1, tracks: [1, 2], axis: LAYOUT_AXIS.column, addIndex: 1 } },
 *   right: { canDrag: true, startPositionIndex: 7, tracksMeta: { main: 2, tracks: [1,2], axis: LAYOUT_AXIS.column, addIndex: 3 } },
 *   top: { canDrag: false, startPositionIndex: 1,  tracksMeta: { main: 0, tracks: [0], axis: LAYOUT_AXIS.row, addIndex: 0 } }
 *   bottom: { canDrag: false, startPositionIndex: 3, tracksMeta: { main: 0, tracks: [0], axis: LAYOUT_AXIS.row, addIndex: 1 } }
 * }
 */
const computeCell = weakMemo((locations: LayoutLocations) => {
  const matrix = computeLayoutMatrix(locations);
  return cellIdMemo(
    (cellId: CellId): Partial<Record<CellDirection, CellMetadata>> => {
      const cell = makeCell(cellId, locations);
      return Object.values(CELL_DIRECTIONS).reduce<Record<string, any>>(
        (result, direction) => {
          result[direction] = computeDirection(cell, direction, matrix);
          return result;
        },
        {}
      );
    }
  );
});

/**
 * Computes the ability to drag a Cell in a given direction, Cell tracks metadata,
 * and the start position index to add the row or column in a given direction.
 */
function computeDirection(
  cell: Cell | null | undefined,
  direction: CellDirection,
  matrix: LayoutMatrix
): CellMetadata {
  const canDrag = canDragCell(cell, direction, matrix);
  return {
    canDrag,
    startPositionIndex: getStartPositionIndex(cell, direction),
    tracksMeta: getCellTracksMetadata(cell, direction),
  };
}

/**
 * Given a Cell computes the start position index to add the row or column (depending
 * on the Cell direction).
 */
function getStartPositionIndex(
  cell: Cell | null | undefined,
  direction: CellDirection
): number | null {
  if (!cell) {
    return null;
  }

  const {gridColumnStart, gridColumnEnd, gridRowStart, gridRowEnd} =
    cell.location;

  /**
   * In LayoutAddPreviewOverlay, we determine which overlay to show on hover of
   * the Add Button with this calculation (startPositionIndex).
   *
   * Side note, the * 2 and - 1 accounts to make sure we
   * are at the correct start or end position of the shown overlay.
   *
   */
  switch (direction) {
    case CELL_DIRECTIONS.LEFT:
      return gridColumnStart * 2 - 1;
    case CELL_DIRECTIONS.RIGHT:
      return gridColumnEnd * 2 - 1;
    case CELL_DIRECTIONS.TOP:
      return gridRowStart * 2 - 1;
    case CELL_DIRECTIONS.BOTTOM:
      return gridRowEnd * 2 - 1;
    default:
      return null;
  }
}

/**
 * Given a Cell evaluates if it can be dragged in a given direction.
 */
function canDragCell(
  cell: Cell | null | undefined,
  direction: CellDirection,
  matrix: LayoutMatrix
): boolean {
  // Early return if no target cells or the direction is TOP or BOTTOM
  if (
    !cell ||
    direction === CELL_DIRECTIONS.TOP ||
    direction === CELL_DIRECTIONS.BOTTOM
  ) {
    return false;
  }

  const {gridColumnStart, gridColumnEnd} = cell.location;

  switch (direction) {
    case DRAG_DIRECTIONS.LEFT:
      // Can drag if the Cell is not at the left edge.
      return gridColumnStart > 1;
    case DRAG_DIRECTIONS.RIGHT: {
      const maxColumn = matrix[0]?.length;

      // We do not expect this to ever happen at runtime because a Layout should
      // always have at least 1 row / 1 column
      if (!maxColumn) return false;

      // Can drag if the Cell is not at the right edge.
      return gridColumnEnd <= maxColumn;
    }
    default:
      return false;
  }
}

/**
 * Given a Cell computes what tracks (rows/columns) the Cell spans across,
 * which of them is the "main" track being resized when dragging, and the index
 * that determines at which location to add the row or column
 * (depending on the Cell direction).
 */
function getCellTracksMetadata(
  cell: Cell | null | undefined,
  direction: CellDirection
): TracksMetadata {
  const meta: TracksMetadata = {
    // The main track's index that is being resized when dragging in this direction.
    main: null,
    // All the tracks indexes that the Cell spans across (if merged Cell).
    tracks: [],
    // The track axis type is 'rows' or 'columns'
    axis: null,
    // The index that determines at which location to add the row or column.
    addIndex: null,
  };
  if (!cell) {
    return meta;
  }

  // Get the indexes of all the tracks the Cell spans across
  const columnCords = makeColumnCoordinatesFromLocation(cell.location);
  const rowCords = makeRowCoordinatesFromLocation(cell.location);

  switch (direction) {
    case CELL_DIRECTIONS.LEFT: {
      // When dragging left, the main column being resized is the first one
      // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number | null'.
      meta.main = columnCords[0];
      meta.tracks = columnCords;
      meta.axis = LAYOUT_AXIS.columns;
      // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number | null'.
      meta.addIndex = columnCords[0];
      break;
    }
    case CELL_DIRECTIONS.RIGHT: {
      // When dragging left, the main column being resized is the last one
      // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number | null'.
      meta.main = columnCords[columnCords.length - 1];
      meta.tracks = columnCords;
      meta.axis = LAYOUT_AXIS.columns;
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      meta.addIndex = columnCords[columnCords.length - 1] + 1;
      break;
    }
    case CELL_DIRECTIONS.TOP: {
      // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number | null'.
      meta.main = rowCords[0];
      meta.tracks = rowCords;
      meta.axis = LAYOUT_AXIS.rows;
      // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number | null'.
      meta.addIndex = rowCords[0];
      break;
    }
    case CELL_DIRECTIONS.BOTTOM: {
      // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number | null'.
      meta.main = rowCords[rowCords.length - 1];
      meta.tracks = rowCords;
      meta.axis = LAYOUT_AXIS.rows;
      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      meta.addIndex = rowCords[rowCords.length - 1] + 1;
      break;
    }
  }

  return meta;
}

export default computeCell;
